import { useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

const FAQS: { question: string; answer: ReactNode }[] = [
  {
    question: "What does COSX do?",
    answer:
      "COSX builds AI agents for regulated industries. Each agent runs on a shared platform with built-in compliance, audit trails, and human oversight. Our products currently serve immigration law (XENI), education (Robolab X), and investment management (Metaroom).",
  },
  {
    question: "How do COSX agents handle compliance?",
    answer:
      "Every agent output passes through governance checks before delivery. This includes compliance gates with configurable rules per vertical, a full audit trail on every action, and mandatory human review for flagged items. The platform is designed around GDPR, ISO 27001, and SOC 2 Type II controls.",
  },
  {
    question: "Is my data secure?",
    answer:
      "All data is encrypted with AES-256 at rest and in transit, stored within UK and EU jurisdictions, and isolated per tenant. We hold Cyber Essentials certification and our architecture is aligned to ISO 27001 and SOC 2 Type II standards. See our platform security page for full details.",
  },
  {
    question: "Can COSX integrate with our existing systems?",
    answer:
      "The platform supports enterprise SSO (SAML, OIDC), API-based integrations, and webhook events. Each agent can connect to your existing tools and data sources. We work with your team to scope integration requirements during onboarding.",
  },
  {
    question: 'What does "Partner with us" mean?',
    answer:
      "We work with firms in regulated industries who want to deploy AI agents or explore a technology partnership. This can range from using one of our existing products to a deeper collaboration. If you're a firm owner thinking about how AI fits into your operations, get in touch and we'll have a conversation.",
  },
  {
    question: "How do I get started?",
    answer: (<>Fill in the form above or email <a href="mailto:hi@cosx.co" className="text-[var(--color-fg)] hover:underline">hi@cosx.co</a>. Tell us about your business and what you're looking for. We typically respond within one business day.</>),
  },
];

const PAD = (n: number) => String(n + 1).padStart(2, "0");

export function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="max-w-3xl">
      {FAQS.map((faq, i) => (
        <div key={i} className="border-t border-[var(--color-border)]">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 py-5 text-left cursor-pointer group"
          >
            <span className="flex items-center gap-3">
              <span className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
                {PAD(i)}
              </span>
              <span className="text-sm font-medium text-[var(--color-fg)] group-hover:text-[var(--color-fg)] transition-colors">
                {faq.question}
              </span>
            </span>
            <span
              className={cn(
                "flex-shrink-0 w-5 h-5 flex items-center justify-center text-[var(--color-fg-tertiary)] transition-transform duration-300",
                open === i && "rotate-45",
              )}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="7" y1="0" x2="7" y2="14" />
                <line x1="0" y1="7" x2="14" y2="7" />
              </svg>
            </span>
          </button>
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: open === i ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed pb-5 pl-8">
                {faq.answer}
              </p>
            </div>
          </div>
        </div>
      ))}
      <div className="border-t border-[var(--color-border)]" />
    </div>
  );
}
